<script setup lang="ts">
import { cents } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';

import { getGiftCertificate } from '@/api/giftCertificates';
import CustomizerCatalogSort from '@/components/customizer/CustomizerCatalogSort.vue';
import CustomizerCategoryFilterControlPanel from '@/components/customizer/CustomizerCategoryFilterControlPanel.vue';
import IngredientCatalog from '@/components/customizer/CustomizerIngredientCatalog.vue';
import CustomizerKeywordFilter from '@/components/customizer/CustomizerKeywordFilter.vue';
import CustomizerMobileModal from '@/components/customizer/CustomizerMobileModal.vue';
import CustomizerTagFilterControlPanel from '@/components/customizer/CustomizerTagFilterControlPanel.vue';
import RedemptionErrorModal from '@/components/customizer/RedemptionErrorModal.vue';
import { Category, SkuSelection, Sort, VolumetricSelection } from '@/lib/customizers/common';
import { DigitalGifting } from '@/lib/customizers/digitalGifting';
import { SkuIngredient, VolumetricIngredient } from '@/lib/customizers/ingredients';
import { Money } from '@/utils/money';
import { reportError } from '@/utils/reportError';

const props = withDefaults(
  defineProps<{
    categoryLevel?: string;
    ingredients: (VolumetricIngredient | SkuIngredient)[];
    maxIngredients: number;
    mode: 'box' | 'mix';
    selections: (VolumetricSelection | SkuSelection | undefined)[];
  }>(),
  {
    categoryLevel: 'lvl1',
  },
);
const emit = defineEmits<{ 'add-ingredient': [string]; 'remove-ingredient': [string] }>();

const activeFilters = ref<string[]>([]);
const activeKeyword = ref<string>();
const activeSort = ref<Sort>(Sort.Popularity);
const rawResults = computed(() => props.ingredients);
const showIngredientsModal = ref(false);

const handleOpenIngredientsModal = () => {
  showIngredientsModal.value = true;
};
const handleCloseIngredientsModal = () => {
  showIngredientsModal.value = false;
};

const activeIngredients = computed(() => {
  if (
    activeFilters.value.length === 0 &&
    !activeKeyword.value &&
    activeSort.value === Sort.Popularity
  ) {
    return rawResults.value;
  }
  const defaultSorted = rawResults.value.filter((ingredient) => {
    const { hierarchicalFacet } = ingredient;
    const categories = [...(hierarchicalFacet.lvl0 ?? []), ...(hierarchicalFacet.lvl1 ?? [])];

    const matchesFilters = activeFilters.value.every(
      (filter) => categories.includes(filter) || ingredient.tags.includes(filter),
    );

    if (activeKeyword.value && activeKeyword.value.length > 1) {
      const nameContainsKeyword = ingredient.name.toLowerCase().indexOf(activeKeyword.value) !== -1;
      const filtersContainKeyword =
        categories.filter((val) => val.toLowerCase().indexOf(activeKeyword.value!) !== -1).length >
        0;
      return matchesFilters && (nameContainsKeyword || filtersContainKeyword);
    }

    return matchesFilters;
  });

  if (activeSort.value === Sort.Popularity) return defaultSorted;

  return [...defaultSorted].sort((a, b) => {
    if ('volumePrice' in a && 'volumePrice' in b) {
      return cents(a.volumePrice) - cents(b.volumePrice);
    }
    if ('singlePiecePrice' in a && 'singlePiecePrice' in b) {
      return cents(a.singlePiecePrice) - cents(b.singlePiecePrice);
    }
    return 0;
  });
});

const tags = computed(() => new Set(props.ingredients.flatMap((ingredient) => ingredient.tags)));
const activeTags = computed(
  () => new Set(activeIngredients.value.flatMap((activeIngredient) => activeIngredient.tags)),
);

const categoryTree = computed(() => {
  const parsedCategories = new Map<string, Set<string> | undefined>();
  props.ingredients.forEach((data) => {
    if (props.categoryLevel === 'lvl1') {
      data.hierarchicalFacet.lvl1?.forEach((item) => {
        const [parent, child] = item.split(' > ');
        const subCategories = parsedCategories.get(parent);
        if (!subCategories) {
          parsedCategories.set(parent, new Set([child]));
        } else {
          subCategories.add(child);
        }
      });
    } else {
      data.hierarchicalFacet.lvl0?.forEach((item) => {
        parsedCategories.set(item, undefined);
      });
    }
  });
  const categories: Category[] = [];
  parsedCategories.forEach((value, key) => {
    categories.push({
      name: key,
      filter: key,
      children: Array.from(value ?? []).map((child) => ({
        name: child,
        filter: `${key} > ${child}`,
        children: [],
      })),
    });
  });
  return categories;
});

const toggleActiveCategory = (category: string) => {
  if (category === 'all') {
    activeFilters.value = [];
    return;
  }

  const active = activeFilters.value.filter((filter) => filter.includes(category));
  if (active.length) {
    activeFilters.value = activeFilters.value.filter((filter) => !filter.includes(category));
  } else if (activeFilters.value.some((filter) => filter === category)) {
    activeFilters.value.splice(activeFilters.value.indexOf(category), 1);
  } else {
    activeFilters.value = [category];
  }
};

const toggleActiveTag = (filter: string) => {
  if (activeFilters.value.includes(filter)) {
    activeFilters.value.splice(activeFilters.value.indexOf(filter), 1);
  } else {
    activeFilters.value.push(filter);
  }
};

const handleAddIngredient = (productKey: string) => {
  emit('add-ingredient', productKey);
};

const handleRemoveIngredient = (productKey: string) => {
  emit('remove-ingredient', productKey);
};

const route = useRoute();

const showDigitalGiftingRedemptionError = ref(false);
const digitalGifting = reactive<DigitalGifting>({
  giftCertificateCode: undefined,
  digitalGiftSku: undefined,
  giftedProduct: {
    sku: undefined,
    name: undefined,
    tier: 'classic',
  },
});

const isDigitalGifting = computed(() => !!digitalGifting.giftCertificateCode);

const prepareRedemptionIfNeeded = async () => {
  const { gc } = route.query;
  if (!gc) {
    return;
  }
  try {
    const response = await getGiftCertificate(gc as string);
    if (!Money.equals(response.initialValue, response.remainingValue)) {
      showDigitalGiftingRedemptionError.value = true;
    }
    digitalGifting.giftCertificateCode = response.code;
    digitalGifting.digitalGiftSku = '3763-01';
    digitalGifting.giftedProduct.sku = '3719-01';
    digitalGifting.giftedProduct.name = 'Gift Box';
    digitalGifting.giftedProduct.tier = 'classic';
  } catch (error: unknown) {
    reportError(error);
    showDigitalGiftingRedemptionError.value = true;
  }
};

const showPrice = computed(() => props.mode === 'mix' && !isDigitalGifting.value);

onMounted(async () => {
  if (props.mode === 'box') {
    await prepareRedemptionIfNeeded();
  }
});
</script>

<template>
  <div
    role="main"
    class="flex custom-min-h bg-nuts-stone-100"
    :data-test="`${mode}-customizer-page`"
  >
    <div class="flex-row flex-auto hidden lg:flex">
      <div class="pl-10 mt-6 w-60 xl:w-72" data-test="customizer-filter">
        <CustomizerCategoryFilterControlPanel
          :activeFilters="activeFilters"
          :catalogCategoryTree="categoryTree"
          class="mb-15"
          @toggle-category="toggleActiveCategory"
        />
        <CustomizerTagFilterControlPanel
          :activeFilters="activeFilters"
          :activeTags
          :tags
          @toggle-filter="toggleActiveTag"
        />
      </div>
      <div class="flex-1 mt-6 mr-8">
        <div class="flex flex-row items-center justify-between">
          <CustomizerKeywordFilter v-model="activeKeyword" class="w-1/2 max-w-sm" />
          <CustomizerCatalogSort v-if="!isDigitalGifting" v-model="activeSort" />
        </div>
        <IngredientCatalog
          :selections="selections"
          :ingredients="activeIngredients"
          :maxIngredients="maxIngredients"
          :mode="mode"
          :showPrice="showPrice"
          class="mt-6"
          @add-ingredient="handleAddIngredient"
          @remove-ingredient="handleRemoveIngredient"
        />
      </div>
    </div>
    <div
      class="w-full overflow-scroll bg-white border-l border-solid lg:h-screen lg:sticky lg:top-0 border-neutral-200 lg:max-w-sm xl:max-w-lg"
    >
      <slot v-bind="{ digitalGifting, handleOpenIngredientsModal }" />
    </div>
    <CustomizerMobileModal
      class="ws-lg:hidden"
      v-model:filters="activeFilters"
      v-model:keyword="activeKeyword"
      v-model:sort="activeSort"
      :activeTags
      :categoryTree="categoryTree"
      :ingredients="activeIngredients"
      :isOpen="showIngredientsModal"
      :maxIngredients="maxIngredients"
      :mode="mode"
      :selections="selections"
      :showPrice="showPrice"
      :tags
      @add-ingredient="handleAddIngredient"
      @close-modal="handleCloseIngredientsModal"
      @remove-ingredient="handleRemoveIngredient"
      @toggle-category="toggleActiveCategory"
      @toggle-filter="toggleActiveTag"
    />
    <RedemptionErrorModal :showError="showDigitalGiftingRedemptionError" />
  </div>
</template>

<style lang="scss" scoped>
.custom-min-h {
  min-height: calc(100vh - 80px); // screen height - nav height
}

.hidden {
  visibility: visible;
}
</style>
